import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { supabaseClient } from '@/configs/supabase'
import {
  getCustomersByType,
  createCustomerWithPricing,
  updateCustomer,
  updateCustomerPricing,
  createCustomerPricing,
  deleteCustomer,
} from '@/supabase/Customer/customer'
import {
  ICustomerWithPricing,
  ICreateCustomerInput,
  IUpdateCustomerInput,
  ICreateCustomerPricingInput,
  IUpdateCustomerPricingInput,
} from '@/supabase/Customer/customer.interface'
import { ICustomerPricingFormValues } from '../interfaces'

// Hook for fetching regular customers
export const useRegularCustomers = () => {
  return useQuery({
    queryKey: ['customers', 'REGULAR'],
    queryFn: () => getCustomersByType('REGULAR', supabaseClient),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  })
}

// Hook for creating a new regular customer with pricing
export const useCreateRegularCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: ICustomerPricingFormValues) => {
      const customerData: ICreateCustomerInput = {
        name: formData.name,
        type: 'REGULAR',
      }

      const pricingData = {
        largeIceCubePriceTHB: formData.largeIceCubePriceTHB,
        smallIceCubePriceTHB: formData.smallIceCubePriceTHB,
        crushedIcePriceTHB: formData.crushedIcePriceTHB,
        isDefaultPricing: false,
      }

      const result = await createCustomerWithPricing(
        customerData,
        pricingData,
        supabaseClient
      )
      if (!result) {
        throw new Error('Failed to create customer')
      }
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', 'REGULAR'] })
      notification.success({
        message: 'Success',
        description: 'New customer created successfully',
        placement: 'topRight',
      })
    },
    onError: (error: any) => {
      const errorMessage =
        error?.message || 'Error occurred while creating customer'
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      })
    },
  })
}

// Hook for updating an existing regular customer
export const useUpdateRegularCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customer,
      formData,
    }: {
      customer: ICustomerWithPricing
      formData: ICustomerPricingFormValues
    }) => {
      // Update customer basic info
      const customerUpdateData: IUpdateCustomerInput = {
        name: formData.name,
      }

      const updatedCustomer = await updateCustomer(
        customer.id,
        customerUpdateData,
        supabaseClient
      )
      if (!updatedCustomer) {
        throw new Error('Failed to update customer')
      }

      // Update or create pricing
      const pricingUpdateData: IUpdateCustomerPricingInput = {
        largeIceCubePriceTHB: formData.largeIceCubePriceTHB,
        smallIceCubePriceTHB: formData.smallIceCubePriceTHB,
        crushedIcePriceTHB: formData.crushedIcePriceTHB,
      }

      let updatedPricing
      if (customer.pricing) {
        // Update existing pricing
        updatedPricing = await updateCustomerPricing(
          customer.id,
          pricingUpdateData,
          supabaseClient
        )
      } else {
        // Create new pricing
        const pricingCreateData: ICreateCustomerPricingInput = {
          customerId: customer.id,
          largeIceCubePriceTHB: formData.largeIceCubePriceTHB,
          smallIceCubePriceTHB: formData.smallIceCubePriceTHB,
          crushedIcePriceTHB: formData.crushedIcePriceTHB,
          isDefaultPricing: false,
        }
        updatedPricing = await createCustomerPricing(
          pricingCreateData,
          supabaseClient
        )
      }

      if (!updatedPricing) {
        throw new Error('Failed to update pricing')
      }

      return {
        ...updatedCustomer,
        pricing: updatedPricing,
      } as ICustomerWithPricing
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', 'REGULAR'] })
      queryClient.invalidateQueries({ queryKey: ['customer', 'default'] })
      notification.success({
        message: 'Success',
        description: 'Customer information updated successfully',
        placement: 'topRight',
      })
    },
    onError: (error: any) => {
      const errorMessage =
        error?.message || 'Error occurred while updating customer information'
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      })
    },
  })
}

// Hook to get regular customers excluding the default customer
export const useRegularCustomersExcludingDefault = () => {
  const { data: customers, ...rest } = useRegularCustomers()

  const filteredCustomers = useMemo(() => {
    if (!customers) return []
    // Filter to show only REGULAR type customers (excluding default)
    return customers.filter(
      customer => customer.id !== 'default' && customer.type === 'REGULAR'
    )
  }, [customers])

  return {
    data: filteredCustomers,
    ...rest,
  }
}

// Hook for deleting a regular customer (soft delete)
export const useDeleteRegularCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (customerId: string) => {
      const result = await deleteCustomer(customerId, supabaseClient)
      if (!result) {
        throw new Error('Failed to delete customer')
      }
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', 'REGULAR'] })
      notification.success({
        message: 'Success',
        description: 'Customer deleted successfully',
        placement: 'topRight',
      })
    },
    onError: (error: any) => {
      const errorMessage =
        error?.message || 'Error occurred while deleting customer'
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      })
    },
  })
}

// Hook for updating customer rankings
export const useUpdateCustomerRanking = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customers,
      customerId,
      direction,
    }: {
      customers: ICustomerWithPricing[]
      customerId: string
      direction: 'up' | 'down'
    }) => {
      // Find the source customer by ID
      const sourceCustomer = customers.find(c => c.id === customerId)
      if (!sourceCustomer || !sourceCustomer.ranking) {
        throw new Error('Source customer not found or has no ranking')
      }

      const sourceRanking = sourceCustomer.ranking
      // Target ranking is one above (up) or one below (down)
      const targetRanking =
        direction === 'up' ? sourceRanking - 1 : sourceRanking + 1

      // Find the customer currently at the target ranking
      const targetCustomer = customers.find(
        c => c.ranking === targetRanking
      )
      if (!targetCustomer) {
        throw new Error('Target customer not found at ranking ' + targetRanking)
      }

      // Swap rankings between the two customers
      const rankingUpdates = [
        { id: sourceCustomer.id, ranking: targetRanking },
        { id: targetCustomer.id, ranking: sourceRanking },
      ]

      // Import the reorderCustomerRankings function
      const { reorderCustomerRankings } = await import(
        '@/supabase/Customer/customer'
      )

      const success = await reorderCustomerRankings(
        rankingUpdates,
        supabaseClient
      )
      if (!success) {
        throw new Error('Failed to update customer rankings')
      }

      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', 'REGULAR'] })
      notification.success({
        message: 'Success',
        description: 'Customer rankings updated successfully',
        placement: 'topRight',
      })
    },
    onError: (error: any) => {
      const errorMessage =
        error?.message || 'Error occurred while updating customer rankings'
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      })
    },
  })
}
